// Single source of truth for the 8 individual bundle product pages.
//
// Each entry feeds <BundleDetailTemplate /> with the bundle's own preview
// items + a unique "What's Included" feature list pulled from the backend
// /api/products response. The shared "Why This Bundle?" (6 tiles) and FAQ
// list live here so every bundle page reads the same — only the bundle name
// is swapped where relevant.

export const SHARED_WHY_THIS_BUNDLE = [
  {
    title: 'Save Time & Money',
    desc: 'Get ready-to-use HR documents that cut hours of manual work and help you move faster on hiring, onboarding, and policy decisions.',
  },
  {
    title: 'Professional, HR-Built Templates',
    desc: 'Created by HR practitioners with real-world experience so you can confidently manage people, paperwork, and process.',
  },
  {
    title: 'Fully Editable & Easy to Customize',
    desc: "Every template comes in Microsoft Word, Google Docs, or Excel where appropriate so you can adjust wording to fit your organization's needs.",
  },
  {
    title: 'Instant Access',
    desc: 'Download your documents immediately after purchase and start using them right away. No waiting, no shipping.',
  },
  {
    title: 'Lifetime Use',
    desc: "One-time purchase. The templates are yours to keep and reuse forever within your business.",
  },
  {
    title: 'Not Legal Advice',
    desc: 'Templates provide general HR guidance and should be reviewed for compliance with your local, state, and federal requirements. Always consult legal counsel before implementing policies or agreements.',
  },
];

export const buildSharedFAQs = (bundleName) => [
  {
    question: 'Are these templates compliant with federal or state laws?',
    answer:
      'These templates follow standard HR best practices. They are not state-specific or legal advice, so please review and customize them to your company requirements and consult legal counsel for compliance questions.',
  },
  {
    question: 'Can I use these templates for multiple clients?',
    answer:
      `The ${bundleName} is licensed for use within your own organization only. If you are an HR consultant or use the templates across multiple client businesses, contact us at hello@drive-hr.com for extended licensing options.`,
  },
  {
    question: 'Are the templates editable?',
    answer:
      'Yes. All templates are fully editable in Microsoft Word or Google Docs. Workbooks and trackers are provided in Microsoft Excel where applicable.',
  },
  {
    question: 'Can I request customizations?',
    answer:
      'Custom document creation is not included with this bundle. Our consulting services are available on request if you need custom documents tailored to your business.',
  },
  {
    question: 'Do you offer refunds?',
    answer:
      'All sales are final. Because our products are delivered digitally and downloaded immediately, DriveHR does not offer refunds. Please review the product details carefully before purchasing.',
  },
];

// Per-bundle configuration. Keys match backend product `id` and the route
// slug. `templateCount` shows up in the hero subhead, first "What's Included"
// bullet, and final CTA so the counts agree wherever they appear.
export const BUNDLES = {
  'hiring-onboarding': {
    name: 'Hiring & Onboarding Bundle',
    templateCount: 35,
    features: [
      '35 Hiring & Onboarding Documents (Word / Google Docs)',
      'Offer Letter Templates (Exempt, Non-Exempt, Contractor)',
      'Job Description Master Template and Job Application Form',
      'Background Check and Reference Forms',
      'New Hire Checklists and Orientation Agenda',
      'I-9 and E-Verify Documentation',
      'Employment Agreements and NDAs',
    ],
    previewItems: [
      { title: 'Offer Letter Templates', desc: 'Professional offer letters for all employment types.' },
      { title: 'Job Description Master Template', desc: 'A flexible JD framework you can tailor to any position.' },
      { title: 'Onboarding Checklists', desc: 'Complete new hire onboarding workflows from day 1 to day 90.' },
    ],
    ctaTagline: 'Get instant access to every hiring and onboarding template and start running a tighter process today.',
  },
  'policies-bundle': {
    name: 'HR Policies Bundle',
    templateCount: 43,
    features: [
      '43 Essential HR Policies (Word / Google Docs)',
      'Anti-Discrimination and Harassment Policies',
      'Remote Work and Social Media Policies',
      'Code of Conduct and Dress Code',
      'Leave Policies (PTO, Sick, FMLA)',
      'Safety and IT Use Policies',
      'State-Compliant Policy Templates',
    ],
    previewItems: [
      { title: 'Anti-Harassment Policy', desc: 'Comprehensive, defensible workplace policy you can adapt to any state.' },
      { title: 'Remote Work Policy', desc: 'Clear guidelines for hybrid and fully remote teams.' },
      { title: 'Code of Conduct', desc: 'Professional standards and expectations that set the tone.' },
    ],
    ctaTagline: 'Get instant access to a complete policy library and put consistent rules in place across your company.',
  },
  'performance-development': {
    name: 'Performance & Development Bundle',
    templateCount: 28,
    features: [
      '28 Performance Management Documents (Word / Google Docs)',
      'Succession Planning Workbook (Excel)',
      '30-60-90 Day Review Templates',
      'Performance Improvement Plans (PIP)',
      'Goal-Setting and Career Development Tools',
      'Training and Mentorship Programs',
      'Disciplinary Action and Recognition Forms',
    ],
    previewItems: [
      { title: 'Succession Planning Workbook', desc: 'A ready-to-use Excel workbook for mapping key roles and successors.' },
      { title: '30-60-90 Day Review Templates', desc: 'Structured review framework for new hires through their first quarter.' },
      { title: 'Performance Improvement Plans', desc: 'Documented PIPs with clear expectations and follow-up steps.' },
    ],
    ctaTagline: 'Get instant access to every performance tool and start running fairer, more consistent reviews today.',
  },
  'benefits-compensation': {
    name: 'Benefits & Compensation Bundle',
    templateCount: 27,
    features: [
      '27 Benefits & Compensation Documents (Word / Google Docs)',
      'Leave Tracking Spreadsheet (Excel)',
      'FMLA and Leave Request Forms',
      '401(k) and Health Insurance Templates',
      "COBRA and Workers' Comp Documentation",
      'Salary Review and Bonus Agreements',
      'Benefits Election and Tracking Tools',
    ],
    previewItems: [
      { title: 'Leave Tracking Spreadsheet', desc: 'Excel spreadsheet to track PTO, sick leave, and FMLA balances accurately.' },
      { title: 'FMLA Leave Request Form', desc: 'Standard FMLA paperwork ready to send to qualifying employees.' },
      { title: 'Salary Review Templates', desc: 'Structured tools for annual compensation reviews and merit increases.' },
    ],
    ctaTagline: 'Get instant access to every benefits and compensation template and keep your administration clean.',
  },
  'termination-offboarding': {
    name: 'Termination & Offboarding Bundle',
    templateCount: 41,
    features: [
      '41 Termination & Offboarding Documents (Word / Google Docs)',
      'Termination Checklists and Letters',
      'Exit Interview and Survey Templates',
      'Severance and Separation Agreements',
      'COBRA and Final Pay Documentation',
      'Property Return and Reference Forms',
    ],
    previewItems: [
      { title: 'Termination Letter Templates', desc: 'Defensible separation letters for voluntary and involuntary exits.' },
      { title: 'Exit Interview Templates', desc: 'Structured exit interviews so you actually learn from departures.' },
      { title: 'Severance Agreement Template', desc: 'Professional severance and release of claims agreement.' },
    ],
    ctaTagline: 'Get instant access to a complete offboarding toolkit and handle every separation professionally.',
  },
  'compliance-legal': {
    name: 'Compliance & Legal Bundle',
    templateCount: 25,
    features: [
      '25 Compliance & Legal Documents (Word / Google Docs)',
      'State-by-State Labor Law Guides',
      'EEOC and OSHA Compliance Tools',
      'I-9 and Classification Checklists',
      'Workplace Posters and Audit Tools',
      'DOL Readiness and Record Retention',
    ],
    previewItems: [
      { title: 'Labor Law Posters', desc: 'Required workplace postings to keep your locations compliant.' },
      { title: 'EEOC Compliance Tools', desc: 'Documentation checklists and audit-ready record templates.' },
      { title: 'I-9 Classification Checklists', desc: 'Step-by-step worker classification with practical examples.' },
    ],
    ctaTagline: 'Get instant access to the compliance toolkit and reduce audit risk without bringing in outside counsel.',
  },
  'culture-dei-engagement': {
    name: 'Culture, DEI & Engagement Bundle',
    templateCount: 24,
    features: [
      '24 Culture & Engagement Documents (Word / Google Docs)',
      'DEI Policy and Survey Templates',
      'Employee Resource Group Guides',
      'Engagement and Recognition Programs',
      'Mental Health and Wellness Policies',
      'Team Building and Remote Engagement Tools',
    ],
    previewItems: [
      { title: 'DEI Policy Templates', desc: 'A foundation for inclusive workplace policies that fit your size.' },
      { title: 'Engagement Survey Templates', desc: 'Ready-made employee engagement and pulse survey tools.' },
      { title: 'Recognition Program Guides', desc: 'Structured employee recognition programs that actually get used.' },
    ],
    ctaTagline: 'Get instant access to every culture and engagement template and build a workplace people stay in.',
  },
  'hr-operations': {
    name: 'HR Operations Bundle',
    templateCount: 22,
    features: [
      '22 HR Operations Documents (Word / Google Docs)',
      'HR Service Request and Email Templates',
      'Organizational Chart and Metrics Dashboard',
      'Payroll and Position Change Forms',
      'Emergency Contact and Incident Reports',
      'HR SOP Manual and Audit Templates',
    ],
    previewItems: [
      { title: 'HR SOP Manual', desc: 'Standard operating procedures so your HR runs the same way every time.' },
      { title: 'Org Chart Templates', desc: 'Professional organizational chart templates with reporting lines.' },
      { title: 'Metrics Dashboard', desc: 'HR analytics and reporting templates to track what matters.' },
    ],
    ctaTagline: 'Get instant access to a full HR operations toolkit and put repeatable process behind your HR work.',
  },
};
